import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";
import { 
  Eye, 
  EyeOff, 
  Send, 
  RefreshCw, 
  Trash2, 
  Plus,
  Search, 
  ChevronDown, 
  ArrowUpRight, 
  PlusIcon,
} from 'lucide-react';

import './wallet.css';
import { fetchWallets } from '../../../actions/payments';
import { addWallet, deleteUserWallet } from "../../../actions/user";
import Loader from '../../../components/Loader/Loader';

const Wallets = ({ userDetails }) => {
    const { allowedCountries, currencyOptions, conversionRates } = useSelector((state) => state.country);
    const userCountryCode = userDetails?.country_code || 'ng';
    const [showBalances, setShowBalances] = useState(false);
    const [loading, setLoading] = useState(true);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [addWalletLoading, setAddWalletLoading] = useState(false);
    const [walletSelect, setWalletSelect] = useState("");
    const [walletAdded, setWalletAdded] = useState(false); // eslint-disable-line no-unused-vars
    const [isEdited, setIsEdited] = useState(false);
    const [currenciesOption, setCurrenciesOption] = useState([]);
    const [userWallets, setUserWallets] = useState([]);   
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [walletToDelete, setWalletToDelete] = useState(null);
    const closeWalletDeleteRef = useRef(null);
    const closeWalletCreateRef = useRef(null);

    const fetchCountryData = async () => {
        const countryData = {};
        setLoading(true);

        for (const countryCode of allowedCountries) {
            try {
                const response = await fetch(`https://restcountries.com/v3.1/alpha/${countryCode}`);
                const data = await response.json();
                const country = data[0];

                countryData[countryCode] = {
                    name: country.name.common,
                    currency: Object.keys(country.currencies)[0],
                    currencySymbol: country.currencies[Object.keys(country.currencies)[0]].symbol || "",
                    flag: country.flags.svg,
                    balance: Number((Math.random() * 100000).toFixed(2)).toLocaleString(),
                    lastTransaction: "Fri, Feb 14, 2025, 6:00pm", // Static for now
                    lastAmount: Math.floor(Math.random() * 2000) - 1000, // Random transaction
                };
            } catch (error) {
                // console.error(`Error fetching data for ${countryCode}:`, error);
            }
        }

        setLoading(false);
    };

    const fetchAllWallets = async () => {
        setLoading(true);
        try {
            const response = await fetchWallets();
            const wallets = response.data.wallets.wallets;
            
            const enrichedWallets = wallets.map((wallet) => {
                const match = currencyOptions.find(opt => opt.currencyCode === wallet.currency_code);
                return {
                    ...wallet,
                    currencyName: match?.currencyName,
                    countryCode: match?.countryCode,
                    flag: match?.flagUrl,
                    currencySymbol: match?.symbol,
                    lastTransaction: wallet?.last_activity ? formatDateString(wallet?.last_activity.created_at) : 'No transaction activity yet.',
                    lastAmount: wallet?.last_activity ? (
                        (wallet?.last_activity.transaction_type === 'credit' ? 1 : -1) * (wallet?.last_activity.amount)
                    ) : 0.00,
                };
            });

            setUserWallets(enrichedWallets);
        } catch (error) {
            // console.error("Error fetching wallets:", error);
        } finally {
            setLoading(false);
        }
    };
    
    const getUserWallets = (wallets) => {
        setLoading(true);

        if (!wallets || wallets.length === 0) return;

        const enrichedWallets = wallets.map((wallet) => {
            const match = currencyOptions.find(opt => opt.currencyCode === wallet.currency_code);
            return {
                ...wallet,
                currencyName: match?.currencyName,
                countryCode: match?.countryCode,
                flag: match?.flagUrl,
                currencySymbol: match?.symbol,
                lastTransaction: wallet?.last_activity ? formatDateString(wallet?.last_activity.created_at) : 'No transaction activity yet.',
                lastAmount: wallet?.last_activity ? (
                    (wallet?.last_activity.transaction_type === 'credit' ? 1 : -1) * (wallet?.last_activity.amount)
                ) : 0.00,
            };
        });
        
        setUserWallets(enrichedWallets);
        setLoading(false);
    };

    useEffect(() => {
        fetchCountryData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowedCountries]);

    useEffect(() => {
        if (currencyOptions.length > 0) {
            fetchAllWallets();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currencyOptions]);

    const toggleBalanceVisibility = () => {
        setShowBalances((prev) => !prev); 
    };

    const handleWalletSelectChange = (e) => {
        const selectedId = e.target.value;
        const selectedOption = currenciesOption.find((opt) => opt.id === selectedId);
        
        setWalletSelect(selectedOption);
        setIsEdited(true);
    };

    const handleAddWallet = async (e) => {
        e.preventDefault();

        if (!isEdited || !walletSelect) {
            toast.error("Select a wallet");
            return;
        }

        const credential = { currency_code: walletSelect?.currencyCode };

        await addWallet(
            credential, 
            setAddWalletLoading, 
            setWalletSelect, 
            toast, 
            setIsEdited, 
            dispatch, 
            fetchAllWallets, 
            setWalletAdded, 
            closeWalletCreateRef
        );
    };

    useEffect(() => {
        const currencyCodes = userDetails?.wallet?.map(wallet => wallet.currency_code) || [];
        const availableCurrencyOptions = currencyOptions.filter(currency => !currencyCodes.includes(currency.currencyCode) && currency.walletSupported);
        setCurrenciesOption(availableCurrencyOptions);
    }, [currencyOptions, userDetails]);

    const getFlagUrl = (countryCode) => {
        if (!countryCode) return "ng"; 
        return `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`;  
    };

    const calculateTotalBalance = () => {
        if (!userWallets.length) return "0.00";
    
        let totalUSD = 0;
        const rates = conversionRates && conversionRates.length > 0 ? conversionRates : [
            { currency_pair: "USD/NGN", fx_rate: 1542 },
            { currency_pair: "USD/GHS", fx_rate: 12.7 },
            { currency_pair: "USD/ZAR", fx_rate: 18.3 },
            { currency_pair: "USD/KES", fx_rate: 110.5 },
            { currency_pair: "USD/EGP", fx_rate: 30.9 },
            { currency_pair: "USD/MWK", fx_rate: 880.5 },
            { currency_pair: "USD/AOA", fx_rate: 515 },
            { currency_pair: "USD/MAD", fx_rate: 9.5 },
        ];
    
        userWallets.forEach((wallet) => {
            const code = wallet.currency_code || wallet.currency;
            const balanceVal = Number(String(wallet.balance).replace(/[^0-9.-]+/g, "")) || 0;
            if (code === "USD") {
                totalUSD += balanceVal;
            } else {
                const currencyPair = `USD/${code}`;
                const conversionRateObj = rates.find(rate => rate.currency_pair === currencyPair);
                if (conversionRateObj) {
                    totalUSD += balanceVal / conversionRateObj.fx_rate;
                } else {
                    totalUSD += balanceVal;
                }
            }
        });
    
        // Convert totalUSD to the user's local currency
        const userWallet = userWallets.find(w => w.countryCode && w.countryCode.toLowerCase() === userCountryCode.toLowerCase()) || userWallets[0];
        const userCurrency = userWallet?.currencySymbol || '₦';
        const userCurrencyCode = userWallet?.currency_code || 'NGN';
    
        let localTotal = totalUSD;
        if (userCurrencyCode !== "USD") {
            const userRateObj = rates.find(rate => rate.currency_pair === `USD/${userCurrencyCode}`);
            localTotal = totalUSD * (userRateObj?.fx_rate || 1);
        }
    
        return `${userCurrency}${formatAmount(localTotal)}`;
    };

    useEffect(() => {
        const handler = setTimeout(() => {
          setDebouncedSearchTerm(searchTerm);
        }, 300);
      
        return () => {
          clearTimeout(handler);
        };
    }, [searchTerm]);
    
    const matchingWallets = userWallets.filter(wallet =>
        (wallet.currency_code && wallet.currency_code.toLowerCase().includes(debouncedSearchTerm.toLowerCase())) ||
        (wallet.currencyName && wallet.currencyName.toLowerCase().includes(debouncedSearchTerm.toLowerCase())) ||
        (wallet.countryCode && wallet.countryCode.toLowerCase().includes(debouncedSearchTerm.toLowerCase()))
    );
      
    const nonMatchingWallets = userWallets.filter(wallet =>
        !matchingWallets.includes(wallet)
    );
      
    const sortedWallets = [...matchingWallets, ...nonMatchingWallets];
      
    const formatAmount = (amount) =>
        new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
    }).format(amount || 0);

    const cleanAndFormatAmount = (amount) => {
        if (amount === undefined || amount === null) return "0.00";
        const cleaned = Number(String(amount).replace(/[^0-9.-]+/g, ""));
        return isNaN(cleaned) ? "0.00" : formatAmount(cleaned);
    };

    const handleWalletDelete = async (walletId, isDefaultWallet)  => {
        if (isDefaultWallet) {
            toast.error("Cannot delete primary wallet");
            return;
        }
        await deleteUserWallet(walletId, setDeleteLoading, toast, dispatch, getUserWallets, closeWalletDeleteRef );
    };
    
    const getCardThemeClass = (currencyCode) => {
        const code = String(currencyCode).toLowerCase();
        if (code === 'ngn' || code === 'ng') return 'wallet__card--ngn';
        if (code === 'usd' || code === 'us') return 'wallet__card--usd';
        if (code === 'eur') return 'wallet__card--eur';
        return 'wallet__card--default';
    };

  return (
    <section className='main p-2 wallets__page position-relative'>
        <div className="container-fluid p-0">
            {/* Total Balance Summary Header */}
            {/* {!loading && userWallets.length > 0 && (
                <div className="total-balance-card mb-4">
                    <div>
                        <div className="total-balance-title">
                            <Wallet size={16} />
                            <span>Total Combined Balance</span>
                        </div>
                        <div className="total-balance-amount">
                            {showBalances ? "******" : calculateTotalBalance()}
                        </div>
                    </div>
                    <button className="balance-visibility-btn" onClick={toggleBalanceVisibility} aria-label="Toggle Balance Visibility">
                        {showBalances ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                </div>
            )} */}

            {/* Filter and Add New Wallet actions */}
            <section className="mt-3">
                <div className="transaction-filter row justify-content-between align-items-center">
                    <div className="col-md-7 col-sm-12 search-box mb-3 mb-md-0">
                        <Search className="search-icon" size={18} />
                        <input type="text" className="form-control" placeholder="Search wallets by code or name" 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="col-md-5 col-sm-12 d-flex justify-content-md-end">
                        <button className="add-btn" data-bs-toggle="modal" data-bs-target="#addWalletModal">
                            <Plus size={20} />
                            <span>Add New Wallet</span>
                        </button>

                        {/* <!-- Add Wallet Modal --> */}
                        <section className="modal fade" id="addWalletModal" tabIndex="-1" aria-labelledby="addWalletModalLabel" aria-hidden="true">
                            <div className="modal-dialog modal-dialog-centered">
                                <div className="modal-content">
                                    <div className="d-flex justify-content-end pe-3 pt-3">
                                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" ref={closeWalletCreateRef}></button>
                                    </div>
                                    <div className="modal-body px-5">
                                        <h4 className="mb-4 text-center">Add Wallet</h4>
                                        <form onSubmit={handleAddWallet}>
                                            <div className="custom-select">
                                                <select className="form-control my-3" value={walletSelect?.id || ""} onChange={handleWalletSelectChange}>
                                                    <option value="">Choose Currency</option>
                                                    {currenciesOption.length > 0 ? currenciesOption.map((options) => (
                                                        <option key={options.id} value={options.id}>
                                                            {options.name}
                                                        </option>
                                                    )) : <option disabled>No currencies available</option>}
                                                </select>
                                                <ChevronDown className="caret-icon" size={18} />
                                            </div>
                                            <button type="submit" className="confirm-walletBtn mt-4" disabled={loading || !isEdited}>
                                                {addWalletLoading ? (
                                                    <div className="spinner-border spinner-border-sm text-light" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                ) : 'Confirm'}
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </section>

            {/* Wallets Showcase Grid */}
            <section className='wallets__showcase mt-4'>
                {loading ? (
                    <div className="d-flex justify-content-center w-100 py-5">
                        <Loader text="Loading wallets..." />
                    </div>
                ) : (
                    userWallets.length > 0 ? (
                        <div className="wallets-grid">
                            {sortedWallets.map((wallet) => (
                                <div key={wallet.id} className={`wallet__card ${getCardThemeClass(wallet.currency_code)}`}>
                                    <div>
                                        {/* Card Top / Header */}
                                        <div className="wallet__card__header">
                                            <div className="wallet__card__currency">
                                                <div className="wallet__card__flag-wrapper">
                                                    <img src={getFlagUrl(wallet.countryCode)} alt={`${wallet.currencyName} flag`} className="currency-flag" />
                                                </div>
                                                <div className="currency-info">
                                                    <h5 className="currency-code text-white">{wallet.currency_code}</h5>
                                                    <span className="currency-name">{wallet.currencyName}</span>
                                                </div>
                                            </div>
                                            {wallet.is_primary && (
                                                <span className="primary-badge">Default</span>
                                            )}
                                        </div>

                                    {/* Card Balance */}
                                    <div className="wallet__card__balance-sec mt-3">
                                        <p className="balance-label">Available Balance</p>
                                        <h2 className="balance-val">
                                            <span>
                                                {wallet.currencySymbol}
                                                {showBalances ? "******" : cleanAndFormatAmount(wallet.available_balance ?? wallet.balance)}
                                            </span>
                                            <button className="eye-icon-btn" onClick={toggleBalanceVisibility} aria-label="Toggle visibility">
                                                {showBalances ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </h2>
                                        <div className="d-flex align-items-center mt-2" style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px', fontWeight: '500', gap: '6px' }}>
                                            <span>Main Bal.</span>
                                            <span>{wallet.currencySymbol}{showBalances ? "******" : cleanAndFormatAmount(wallet.balance)}</span>
                                        </div>
                                    </div>
                                    </div>

                                    {/* Card Footer / Last Activity & Actions */}
                                    <div>
                                        <p className="last-transaction">
                                            {wallet?.last_activity ? (
                                                <>
                                                    <span>Activity:</span>
                                                    <span className="balance-indicator ms-1">
                                                        <ArrowUpRight className={wallet.lastAmount < 0 ? "negative__bal" : "positive__bal"} size={14} /> 
                                                        <span className={wallet.lastAmount < 0 ? "negative__bal" : "positive__bal"}>
                                                            {wallet.lastAmount < 0 ? `-${wallet.currencySymbol}${formatAmount(Math.abs(wallet.lastAmount))}` : `+${wallet.currencySymbol}${formatAmount(wallet.lastAmount)}`}
                                                        </span>
                                                    </span>
                                                </>
                                            ) : (
                                                <span>No recent transactions</span>
                                            )}
                                        </p>

                                        {/* Actions */}
                                        <div className="wallet-card-actions">
                                            {wallet.is_primary && (
                                                <button onClick={() => navigate('/user/add_money')} className="wallet_action_btn send-money">
                                                    <PlusIcon size={14} />
                                                    <span>Add</span>
                                                </button>
                                            )}
                                            <button 
                                                onClick={() => navigate('/user/converter', { state: { backToCalDefault: { sendCurrency: wallet.currency_code } } })} 
                                                className="wallet_action_btn convert-funds"
                                            >
                                                <RefreshCw size={14} />
                                                <span>Convert</span>
                                            </button>
                                            <button onClick={() => navigate('/user/send_money')} className="wallet_action_btn send-money">
                                                <Send size={14} />
                                                <span>Send</span> 
                                            </button>
                                            {!wallet.is_primary && wallet.balance == 0 && (
                                                <button className="wallet_action_btn delete__wallet" data-bs-toggle="modal" data-bs-target="#deleteWalletModal" onClick={() => setWalletToDelete(wallet)}>
                                                    <Trash2 size={14} />
                                                    <span>Delete</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-center text-muted py-5">No wallets found.</p>
                    )
                )}

                {/* <!-- Delete Wallet Modal --> */}
                <section className="modal fade" id="deleteWalletModal" tabIndex="-1" aria-labelledby="deleteWalletModalLabel" aria-hidden="true">
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="d-flex justify-content-end p-3">
                                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" ref={closeWalletDeleteRef}></button>
                            </div>
                            <div className="modal-body mx-4">
                                <div className="text-center mb-3">
                                    <h4 className="mb-2">Delete Wallet</h4>
                                    <p>Are you sure you want to delete your <strong>{walletToDelete?.currency_code} ({walletToDelete?.currencyName})</strong> wallet? This action cannot be undone.</p>
                                </div>
                                <div className="d-flex align-items-center gap-3 justify-content-between mt-4">
                                    <button className="btn primary__btn__outline w-50" data-bs-dismiss="modal" aria-label="Close">No, Cancel</button>
                                    <button className="btn primary__btn w-50" onClick={() => handleWalletDelete(walletToDelete.id, walletToDelete.is_primary)}>
                                        {deleteLoading ? (
                                            <div className="spinner-border spinner-border-sm text-light" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        ):(
                                            <span className="">Yes, Delete</span>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </section>
        </div>
    </section>
  )
}

export const formatDateString = (dateString) => {
    return new Intl.DateTimeFormat("en-US", {
        weekday: "short",
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    }).format(new Date(dateString));
}

export default Wallets